import { useState, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useDoctorUnavailability, useUnavailability, useDeleteUnavailability } from "@/hooks/useUnavailability";
import { useDoctors } from "@/hooks/useDoctors";
import { getDoctorName } from "@/utils/dataLookups";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Trash2, Edit, AlertCircle, Users, User } from "lucide-react";
import { format, parseISO, isAfter, startOfDay } from "date-fns";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { showSuccess, showError } from "@/utils/toastHelpers";

export default function DoctorUnavailabilityView({ 
  onEdit, 
  onAddNew,
  showOnlyFuture = true 
}) {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState("list"); // "list", "grouped", or "byDoctor"
  const [selectedDoctor, setSelectedDoctor] = useState("all");
  
  // Fetch doctors list
  const { data: doctors = [] } = useDoctors(user?.practiceId);
  
  // Fetch unavailability based on selected doctor
  const { 
    data: doctorUnavailability = [], 
    isLoading: doctorLoading 
  } = useDoctorUnavailability(
    selectedDoctor !== "all" ? selectedDoctor : null
  );
  
  // Fetch all unavailability when "all" is selected
  const {
    data: allUnavailability = [],
    isLoading: allLoading
  } = useUnavailability(
    selectedDoctor === "all" ? {} : { doctorId: selectedDoctor }
  );
  
  const unavailabilityData = selectedDoctor === "all" ? allUnavailability : doctorUnavailability;
  const isLoading = selectedDoctor === "all" ? allLoading : doctorLoading;
  const error = null;
  
  const deleteUnavailabilityMutation = useDeleteUnavailability();

  // Filter and sort unavailability blocks
  const processedUnavailability = useMemo(() => {
    if (!unavailabilityData || unavailabilityData.length === 0) return [];
    
    let filtered = [...unavailabilityData];
    
    // Filter for future blocks if requested
    if (showOnlyFuture) {
      const today = startOfDay(new Date());
      filtered = filtered.filter(block => {
        const blockDate = parseISO(block.date || block.start_time);
        return isAfter(blockDate, today) || format(blockDate, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");
      });
    }
    
    // Sort by date and start time
    return filtered.sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.start_time || '00:00'}`);
      const dateB = new Date(`${b.date}T${b.start_time || '00:00'}`);
      return dateA - dateB;
    });
  }, [unavailabilityData, showOnlyFuture]);

  // Group unavailability by date for better visualization
  const groupedByDate = useMemo(() => {
    const grouped = {};
    processedUnavailability.forEach(block => {
      const date = block.date || format(parseISO(block.start_time), "yyyy-MM-dd");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(block);
    });
    return grouped;
  }, [processedUnavailability]);

  // Group unavailability by doctor
  const groupedByDoctor = useMemo(() => {
    const grouped = {};
    processedUnavailability.forEach(block => {
      const doctorKey = getDoctorName(block.doctor_id, doctors) || "Unknown Doctor";
      if (!grouped[doctorKey]) {
        grouped[doctorKey] = [];
      }
      grouped[doctorKey].push(block);
    });
    // Sort blocks within each doctor group by date
    Object.keys(grouped).forEach(doctor => {
      grouped[doctor].sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.start_time || '00:00'}`);
        const dateB = new Date(`${b.date}T${b.start_time || '00:00'}`);
        return dateA - dateB;
      });
    });
    return grouped;
  }, [processedUnavailability, doctors]);

  const handleDelete = async (blockId) => {
    if (window.confirm("Are you sure you want to remove this unavailability block?")) {
      try {
        await deleteUnavailabilityMutation.mutateAsync(blockId);
        showSuccess("Unavailability block removed successfully");
      } catch (error) {
        console.error("Failed to delete unavailability:", error);
        showError("Failed to delete unavailability block", error.message);
      }
    }
  };

  const formatTimeRange = (startTime, endTime) => {
    return `${startTime} - ${endTime}`;
  };

  const getReasonBadgeVariant = (reason) => {
    const reasonLower = reason?.toLowerCase() || "";
    if (reasonLower.includes("vacation") || reasonLower.includes("holiday")) {
      return "secondary";
    }
    if (reasonLower.includes("meeting") || reasonLower.includes("conference")) {
      return "default";
    }
    if (reasonLower.includes("lunch") || reasonLower.includes("break")) {
      return "outline";
    }
    if (reasonLower.includes("emergency") || reasonLower.includes("urgent")) {
      return "destructive";
    }
    return "default";
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">Loading unavailability...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load unavailability blocks. Please try again.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            View Unavailability
            {processedUnavailability.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {processedUnavailability.length} blocks
              </Badge>
            )}
          </CardTitle>
          <div className="flex gap-2">
            <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
              <SelectTrigger className="w-[200px]" data-testid="unavail-view-doctor-filter">
                <SelectValue placeholder="Select doctor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    All Doctors
                  </div>
                </SelectItem>
                {user?.id && (
                  <SelectItem value={user.id}>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      My Unavailability
                    </div>
                  </SelectItem>
                )}
                {doctors
                  .filter(doc => doc.id !== user?.id)
                  .map(doctor => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      {doctor.name || doctor.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-[150px]" data-testid="unavail-view-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="list" data-testid="unavail-view-mode-list">List View</SelectItem>
                <SelectItem value="grouped" data-testid="unavail-view-mode-grouped">Group by Date</SelectItem>
                <SelectItem value="byDoctor" data-testid="unavail-view-mode-by-doctor">Group by Doctor</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={onAddNew} size="sm" data-testid="unavail-add-block-btn">
              Add Block
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {processedUnavailability.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No unavailability blocks scheduled</p>
            <Button onClick={onAddNew} variant="outline">
              Block Your First Time Slot
            </Button>
          </div>
        ) : viewMode === "list" ? (
          // List View
          <div className="space-y-3">
            {processedUnavailability.map((block, index) => (
              <div
                key={block.id}
                data-testid={`unavail-block-row-${index}`}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">
                      {format(parseISO(block.date || block.start_time), "EEEE, MMMM d, yyyy")}
                    </span>
                    {selectedDoctor === "all" && block.doctor_id && (
                      <Badge variant="outline" className="ml-2">
                        <User className="h-3 w-3 mr-1" />
                        {getDoctorName(block.doctor_id, doctors)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {formatTimeRange(block.start_time, block.end_time)}
                    </span>
                    {block.reason && (
                      <Badge variant={getReasonBadgeVariant(block.reason)}>
                        {block.reason}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(block)}
                    data-testid={`unavail-edit-${index}`}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(block.id)}
                    data-testid={`unavail-delete-${index}`}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === "grouped" ? (
          // Grouped by Date View
          <div className="space-y-6">
            {Object.entries(groupedByDate).map(([date, blocks]) => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <h3 className="font-semibold text-gray-700">
                    {format(parseISO(date), "EEEE, MMMM d, yyyy")}
                  </h3>
                  <Badge variant="outline" className="ml-2">
                    {blocks.length} block{blocks.length > 1 ? "s" : ""}
                  </Badge>
                </div>
                
                <div className="ml-6 space-y-2">
                  {blocks.map((block) => (
                    <div 
                      key={block.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">
                          {formatTimeRange(block.start_time, block.end_time)}
                        </span>
                        {selectedDoctor === "all" && block.doctor_id && (
                          <Badge variant="outline" className="text-xs">
                            {getDoctorName(block.doctor_id, doctors)}
                          </Badge>
                        )}
                        {block.reason && (
                          <Badge variant={getReasonBadgeVariant(block.reason)} className="text-xs">
                            {block.reason}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(block)}
                          className="text-blue-600 hover:text-blue-700 h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(block.id)}
                          className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Grouped by Doctor View
          <div className="space-y-6">
            {Object.entries(groupedByDoctor).map(([doctorName, blocks]) => (
              <div key={doctorName}>
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-4 w-4 text-gray-500" />
                  <h3 className="font-semibold text-gray-700">
                    {doctorName}
                  </h3>
                  <Badge variant="outline" className="ml-2">
                    {blocks.length} block{blocks.length > 1 ? "s" : ""}
                  </Badge>
                </div>
                
                <div className="ml-6 space-y-2">
                  {blocks.map((block) => (
                    <div 
                      key={block.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium">
                          {format(parseISO(block.date || block.start_time), "MMM d, yyyy")}
                        </span>
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">
                          {formatTimeRange(block.start_time, block.end_time)}
                        </span>
                        {block.reason && (
                          <Badge variant={getReasonBadgeVariant(block.reason)} className="text-xs">
                            {block.reason}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(block)}
                          className="text-blue-600 hover:text-blue-700 h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(block.id)}
                          className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}